import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Play, UserCircle, MapPin, CheckCircle, Clock, XCircle, Path as PathIcon } from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending: "bg-[#E5E2D9] text-[#5C5A55]",
  in_progress: "bg-[#F9E4B7] text-[#7A5A11]",
  completed: "bg-[#D4E0D1] text-[#1A3317]",
  missed: "bg-[#F4C7B9] text-[#7A2E18]",
};
const STATUS_ICON = { pending: Clock, in_progress: Play, completed: CheckCircle, missed: XCircle };

function today() {
  const d = new Date(); return d.toISOString().slice(0, 10);
}

export default function Operations() {
  const [date, setDate] = useState(today());
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [assignStaff, setAssignStaff] = useState("");

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get(`/tasks?date=${date}`), api.get("/staff"),
      ]);
      setTasks(t.data); setStaff(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const generate = async () => {
    try {
      const r = await api.post("/ops/plan", { date });
      toast.success(`Generated ${r.data.created} tasks (${r.data.skipped} skipped)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === tasks.length) setSelected(new Set());
    else setSelected(new Set(tasks.map(t => t.id)));
  };

  const assign = async () => {
    if (selected.size === 0) return toast.error("Select tasks first");
    if (!assignStaff) return toast.error("Select staff");
    try {
      await api.post("/tasks/assign", { task_ids: Array.from(selected), staff_id: assignStaff });
      toast.success(`Assigned ${selected.size} tasks`);
      setSelected(new Set()); setAssignStaff(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markStatus = async (taskId, status) => {
    try { await api.post(`/tasks/${taskId}/status`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const sn = (id) => staff.find(s => s.id === id)?.name || "—";

  const byStatus = tasks.reduce((acc, t) => { acc[t.status] = (acc[t.status]||0)+1; return acc; }, {});

  return (
    <AppShell title="Operations Engine" subtitle="Module 5"
      actions={
        <div className="flex gap-2">
          <input type="date" value={date} onChange={e=>setDate(e.target.value)}
                 data-testid="ops-date"
                 className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <button onClick={generate} data-testid="btn-generate-plan"
                  className="h-10 px-4 bg-[#C25D38] hover:bg-[#A74E2F] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
            <PathIcon size={14} weight="bold"/>Generate plan
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-4 gap-4 mb-6">
        {["pending", "in_progress", "completed", "missed"].map(s => {
          const Icon = STATUS_ICON[s];
          return (
            <div key={s} className="stat-card" data-testid={`kpi-${s}`}>
              <div className="flex items-center gap-3">
                <Icon size={18} color="#2D5A27" weight="duotone"/>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">{s.replace("_"," ")}</div>
              </div>
              <div className="font-heading text-3xl mt-3">{byStatus[s] || 0}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
            {selected.size > 0 ? `${selected.size} selected` : `${tasks.length} tasks`}
          </div>
          <div className="flex-1"/>
          {selected.size > 0 && (
            <div className="flex gap-2">
              <select value={assignStaff} onChange={e=>setAssignStaff(e.target.value)} data-testid="ops-staff-select"
                      className="h-9 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
                <option value="">Assign to staff…</option>
                {staff.map(s=><option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
              </select>
              <button onClick={assign} data-testid="btn-assign"
                      className="h-9 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm">Assign</button>
            </div>
          )}
        </div>
        <table className="w-full text-sm" data-testid="tasks-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-4 py-3"><input type="checkbox" checked={selected.size === tasks.length && tasks.length>0} onChange={toggleAll} className="accent-[#2D5A27]"/></th>
              <th className="px-6 py-3">Seq</th>
              <th className="px-6 py-3">Customer</th>
              <th className="px-6 py-3">GPS</th>
              <th className="px-6 py-3">Slot</th>
              <th className="px-6 py-3">Assigned</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 && <tr><td colSpan={8} className="px-6 py-16 text-center text-[#8A8780]">No tasks for {date}. Click Generate plan to create them from approved customers + schedules.</td></tr>}
            {tasks.map((t,i) => {
              const Icon = STATUS_ICON[t.status] || Clock;
              return (
                <tr key={t.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                  <td className="px-4 py-3"><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} className="accent-[#2D5A27]" data-testid={`task-check-${t.id}`}/></td>
                  <td className="px-6 py-3 font-mono text-xs">{t.sequence || "—"}</td>
                  <td className="px-6 py-3 font-medium">{t.customer_name}</td>
                  <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">
                    <MapPin size={10} className="inline mr-1 text-[#2D5A27]"/>{t.latitude?.toFixed(4)},{t.longitude?.toFixed(4)}
                  </td>
                  <td className="px-6 py-3 font-mono text-xs">{t.start_time}–{t.end_time}</td>
                  <td className="px-6 py-3 text-[#5C5A55]">{sn(t.assigned_staff_id)}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em] ${STATUS_STYLE[t.status]}`}>
                      <Icon size={10} weight="fill"/> {t.status.replace("_"," ")}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    {t.status === "pending" && <button data-testid={`start-${t.id}`} onClick={() => markStatus(t.id, "in_progress")} className="text-[#2D5A27] text-xs mr-3">Start</button>}
                    {t.status === "in_progress" && <button data-testid={`complete-${t.id}`} onClick={() => markStatus(t.id, "completed")} className="text-[#2D5A27] text-xs mr-3">Complete</button>}
                    {["pending","in_progress"].includes(t.status) && <button data-testid={`miss-${t.id}`} onClick={() => markStatus(t.id, "missed")} className="text-[#C25D38] text-xs">Miss</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
